import { useEffect, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import BarChartIcon from '@mui/icons-material/BarChart'
import { useSelector, useDispatch } from 'react-redux'
import Beacon4KpiLineChart from './charts/Beacon4KpiLineChart'
import Beacon4PerformanceAdminDialog from './admin/Beacon4PerformanceAdminDialog'
import Beacon4PerformanceAggregationTable from './Beacon4PerformanceAggregationTable'
import {
  selectBeacon4PerformanceDashboardByTech,
  selectBeacon4PerformanceLoading,
} from '../reducers/Beacon4PerformanceKpiDataReducer'
import {
  selectBeacon4PerfTech,
  selectBeacon4PerfMainKpi,
  selectBeacon4VisibleSeriesLeft,
  selectBeacon4VisibleSeriesRight,
  selectBeacon4AdminDialogOpen,
  setBeacon4AdminDialog,
} from '../reducers/Beacon4FiltersReducer'
import { getColorForKey } from './filters/Beacon4PerformanceLegendPanel'

function EmptyState({ title, subtitle }) {
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: 400, gap: 1.5, color: 'text.disabled',
    }}>
      <BarChartIcon sx={{ fontSize: 48, opacity: 0.3 }} />
      <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: 11 }}>{subtitle}</Typography>}
    </Box>
  )
}

// Convert N single-series DTOs into a chart DTO with N overlaid series.
// `seriesOffset` is the index of the first series in the global colour palette,
// so that left- and right-panel colours don't collide.
function toChartDto(dtos, title, visibleSeries, seriesOffset = 0) {
  if (!dtos || dtos.length === 0) return null

  // Union of all date labels across all DTOs (sorted)
  const labelSet = new Set()
  dtos.forEach(d => (d.labels ?? []).forEach(l => labelSet.add(l)))
  const labels = [...labelSet].sort()

  // Build aligned series. Each series keeps its own colour, matching the legend.
  const series = dtos
    .map((d, i) => {
      const seriesKey = d.kpiName
      // Apply legend visibility filter (null = all visible)
      if (visibleSeries !== null && !visibleSeries.includes(seriesKey)) return null
      const map = {}
      ;(d.labels ?? []).forEach((l, j) => { map[l] = (d.data ?? [])[j] })
      return {
        key:    seriesKey,
        label:  d.kpiName,
        unit:   d.unit,
        color:  getColorForKey(seriesKey, seriesOffset + i),
        data:   labels.map(l => (l in map ? map[l] : null)),
      }
    })
    .filter(Boolean)

  return {
    kpiKey:   title,
    kpiName:  title,
    unit:     dtos[0]?.unit ?? '',
    isRate:   dtos[0]?.isRate ?? false,
    slaGreen: null,
    slaRed:   null,
    labels,
    series,
  }
}

export default function Beacon4PerformanceView({ externalRole }) {
  const dispatch  = useDispatch()
  const adminOpen = useSelector(selectBeacon4AdminDialogOpen)
  const canAdmin = externalRole === 'Administrator' || externalRole === 'Manager'

  const dashboardByTech = useSelector(selectBeacon4PerformanceDashboardByTech)
  const loading         = useSelector(selectBeacon4PerformanceLoading)
  const tech            = useSelector(selectBeacon4PerfTech)
  const mainKpi         = useSelector(selectBeacon4PerfMainKpi)
  const visibleLeft     = useSelector(selectBeacon4VisibleSeriesLeft)
  const visibleRight    = useSelector(selectBeacon4VisibleSeriesRight)

  // Chart height as a NUMBER (not "100%"), recomputed when the window resizes.
  // We use a pixel height instead of height:100% inside a flex container —
  // Chart.js's internal resize on a percentage-height canvas inside a flex
  // box can shrink the box by 1px per frame, creating a slow-collapse loop.
  // A fixed-px height lets the chart measure once and stop.
  const computeChartH = () => {
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800
    // ~42% of viewport, clamped to [320, 480]
    return Math.max(320, Math.min(480, Math.round(vh * 0.42)))
  }
  const [chartH, setChartH] = useState(computeChartH)
  useEffect(() => {
    const onResize = () => setChartH(computeChartH())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  if (!mainKpi) {
    return <EmptyState
      title="Select a KPI and press Apply"
      subtitle={`Choose a Tech and KPI in the filter bar to load the ${tech} dashboard`}
    />
  }

  const data = dashboardByTech?.[tech]?.[mainKpi]

  if (!data && !loading) {
    return <EmptyState
      title="Press Apply to load data"
      subtitle={`${tech} · ${mainKpi.toUpperCase()}`}
    />
  }

  const leftPanel  = data?.leftPanel  ?? []
  const rightPanel = data?.rightPanel ?? []

  const leftDto  = toChartDto(leftPanel,  'KPI Performance', visibleLeft,  0)
  const rightDto = toChartDto(rightPanel, 'KPI Failures',    visibleRight, leftPanel.length)

  const mainTitle = leftPanel?.[0]?.kpiName ?? mainKpi.toUpperCase()

  return (
    <Box sx={{
      position: 'relative',
      // Parent (App.chartArea) is a flex column with defined height when in
      // Performance mode, so flex: 1 + minHeight: 0 is enough to fill it.
      // We OWN the scroll inside — the table at the bottom keeps its
      // pagination footer visible because its height is bounded by us.
      flex: 1, minHeight: 0,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Loading indicator */}
      {loading && (
        <Box sx={{
          position: 'absolute', top: 8, right: 8, zIndex: 5,
          display: 'flex', alignItems: 'center', gap: 1,
          bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 1, px: 1.5, py: 0.5,
          border: '1px solid', borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}>
          <CircularProgress size={12} />
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Loading…</Typography>
        </Box>
      )}

      {/* Admin dialog — opened by the Manage KPIs button in the FilterBar */}
      {canAdmin && (
        <Beacon4PerformanceAdminDialog open={adminOpen} onClose={() => dispatch(setBeacon4AdminDialog(false))} />
      )}

      {/* Two side-by-side panels — viewport-aware height in pixels.
          We deliberately avoid percentage heights here: Chart.js's resize
          on a percentage-sized canvas inside a flex column produces a
          slow-collapse feedback loop (the box shrinks by ~1px per frame).
          A numeric chartH lets the chart measure once and stop. */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: rightPanel.length > 0 ? { xs: '1fr', lg: '1fr 1fr' } : '1fr',
        gap: 1.5,
        width: '100%',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        {/* LEFT: Main KPI + sub-components */}
        <Box data-chart-title={`Performance ${tech} ${mainKpi}`} sx={{ minWidth: 0 }}>
          <Beacon4KpiLineChart
            title={`KPI Performance — ${mainTitle}`}
            seriesDto={leftDto}
            kpiKey={`perf_left_${tech}_${mainKpi}`}
            height={chartH}
          />
        </Box>

        {/* RIGHT: Availability + raw counter failures */}
        {rightPanel.length > 0 && (
          <Box data-chart-title={`Failures ${tech} ${mainKpi}`} sx={{ minWidth: 0 }}>
            <Beacon4KpiLineChart
              title="KPI Failures"
              seriesDto={rightDto}
              kpiKey={`perf_right_${tech}_${mainKpi}`}
              height={chartH}
            />
          </Box>
        )}
      </Box>

      {/* BOTTOM: Aggregation Mode breakdown table — flex-grows to fill the
          remaining vertical space. minHeight: 0 is mandatory or the flex child
          refuses to shrink below its content. */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', mt: 1.5 }}>
        <Beacon4PerformanceAggregationTable />
      </Box>
    </Box>
  )
}